import { useEffect } from "react";
import { useDispatch } from "react-redux";

import { updateChartByPath } from "store/features";

import { useChartEntities } from "./useChartEntities";

/**
 * Turns a chart's legend ON when the chart starts showing something the map's
 * legend does not describe.
 *
 * Two triggers use this today, and they share a cause — the chart is no longer
 * grouped the way the map is, so nothing on screen names what its colours mean:
 *
 *  * **Chart Focus** regroups this chart alone, so its bins are not the map's.
 *    The wells charts have always done this (`useChartVsGlobalSettings`); the lean
 *    widgets do not use that hook.
 *  * **All Statuses** (Well Status Current) stacks one segment per STATUS across
 *    the bins, so the map's bin legend describes the wrong dimension entirely.
 *
 * **Opens only — never closes.** When the condition goes false the legend is left
 * as it is, and the effect is keyed on the condition alone, so a legend the user
 * closes while the condition still holds stays closed until they change modes
 * again. Taking away a control someone is holding is worse than leaving one open.
 *
 * ## Why there is no "respect a saved-off legend" guard
 *
 * There was one, twice, and it cannot be made to work: **nothing distinguishes a
 * legend the user deliberately closed from one that has simply never been
 * opened.** Both are `features.legend.active === false`, and that is also what
 * the widget TEMPLATE carries (`BASE_CHART_FEATURES.legend` spreads
 * `DEFAULT_FEATURE_ENTITY`, which is `active: false`, overriding only `show` and
 * `enabled`).
 *
 * Both attempts failed on that ambiguity from opposite ends. Keyed on the flag
 * alone, every freshly dropped widget looked like a deliberate close and the
 * feature never fired at all. Narrowed to widgets restored from a save, it still
 * caught every dashboard saved before this feature existed — which is all of them
 * — because the legend was closed at save time precisely because nothing had ever
 * opened it. The guard was most aggressive for the people using the widget most.
 *
 * The requirement product actually asked for is unconditional ("when the user
 * turns on chart specific focus, the legend needs to be turned on as well"), so
 * that is what this does. If a saved-off preference is ever wanted, it needs a
 * record of the user CLOSING the legend — an explicit act — not the absence of an
 * open one.
 *
 * @param shouldOpen the combined condition, as one boolean.
 */
export function useOpenLegendWhen(id: string, shouldOpen: boolean) {
  const dispatch = useDispatch();
  const { chartLegend } = useChartEntities(id);

  const isLegendActive = chartLegend?.active ?? false;

  useEffect(() => {
    // On mount the chart entity does not exist yet — WellStatusChartWrapper
    // declares this hook AHEAD of its createChart effect, so `chartFocus` and
    // `request` both read from DEFAULT_CHART_ENTITIES and `shouldOpen` is false on
    // that pass whatever the widget was saved with. The condition becoming true on
    // a later render is the normal path, not an edge case.
    if (!shouldOpen) return;
    if (isLegendActive) return;

    dispatch(updateChartByPath({ id, path: "features.legend.active", value: true }));
    // Keyed on the condition alone: this reacts to the condition becoming true,
    // not to the legend being switched off afterwards. Adding isLegendActive here
    // would re-open a legend the user had just closed while the condition held.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldOpen, id]);
}
